import React from 'react';
import type { CustomCellRendererProps } from 'ag-grid-react';
import type { BulkManageRow, HistoricalWarningOrError } from '../types';
import { assertExhaustive } from 'venn-utils';
import { Icon } from 'venn-ui-kit';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { CashIcon } from '../../common/CashIcon';
import { OptionIcon } from '../../common/OptionIcon';
import { AssetBadge } from '../../asset-badge';
import { TruncatedName } from './TruncatedName';

const TooltipIcon = ({ historicalError }: { historicalError: HistoricalWarningOrError }) => {
  switch (historicalError._type) {
    case 'error':
      return <Icon type="triangle-exclamation" className="text-venn-error" iconLabel="Cell historical error icon" />;
    case 'warning':
      return <Icon type="diamond-exclamation" className="text-venn-orange" iconLabel="Cell historical warning icon" />;
    default:
      return assertExhaustive(historicalError);
  }
};

const getTooltipContent = (historicalError: HistoricalWarningOrError) => {
  switch (historicalError._type) {
    case 'error':
      switch (historicalError.value) {
        case 'ALLOCATION_DATE_OUTSIDE_SUBJECT_RANGE':
        case 'INVESTMENT_ALLOCATIONS_NOT_AVAILABLE':
        case 'EARLIEST_ALLOCATION_DATE_PREDATES_SUBJECT_START_DATE':
        case 'INVESTMENT_RETURNS_NOT_AVAILABLE':
          return 'Returns must be available for all portfolio allocations.';
        default:
          return assertExhaustive(historicalError);
      }
    case 'warning':
      switch (historicalError.value) {
        case 'LATEST_ALLOCATION_DATE_EXCEEDS_SUBJECT_RETURNS_END_DATE':
          return 'You can upload additional returns data or add a proxy to increase this investment’s analysis range.';
        default:
          return assertExhaustive(historicalError);
      }
    default:
      return assertExhaustive(historicalError);
  }
};
type NameCellRendererProps = CustomCellRendererProps<BulkManageRow, BulkManageRow['name']> & {
  setRowWithOpenTooltip?: (rowId: string | undefined) => void;
  /* This component is used in both Portfolio Allocations and Portfolio Management
   * tabs on Manage Data page, but we only show error on Portfolio Management tab.
   * This flag allows us to control that.
   */
  shouldShowHistoricalError: boolean;
};
export const NameCellRenderer = ({
  data,
  value,
  setRowWithOpenTooltip,
  shouldShowHistoricalError,
}: NameCellRendererProps) => {
  const [openTooltip, setOpenTooltip] = React.useState(false);

  if (!data) {
    return value;
  }

  switch (data.rowType) {
    case 'investment':
      switch (data.allocationType) {
        case 'historical':
          return data.historicalError && shouldShowHistoricalError ? (
            <div className="flex min-w-0 items-center gap-[3px]">
              <Tooltip
                onOpenChange={(open) => {
                  setRowWithOpenTooltip?.(open ? data?.rowId : undefined);
                  setOpenTooltip(open);
                }}
                open={openTooltip}
              >
                <TooltipTrigger>
                  <TooltipIcon historicalError={data.historicalError} />
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContentWithArrow>{getTooltipContent(data.historicalError)}</TooltipContentWithArrow>
                </TooltipPortal>
              </Tooltip>
              <TruncatedName name={value} fullName={data.name} />
            </div>
          ) : (
            <TruncatedName name={value} fullName={data.name} />
          );

        case 'proforma': {
          if (data.investmentType === 'cash') {
            return (
              <TruncatedName name={value} fullName={data.name}>
                <CashIcon isNegativeAssets={(data.allocation ?? 0) < 0} />
              </TruncatedName>
            );
          }

          if (data.investmentType === 'option') {
            const symbolSuffix = data.symbol ? ` (${data.symbol})` : '';
            return (
              <TruncatedName
                name={
                  <>
                    {value}
                    {symbolSuffix}
                  </>
                }
                fullName={`${data.name}${symbolSuffix}`}
                className="gap-[2px]"
              >
                {data.optionData?.optionType && <OptionIcon optionType={data.optionData.optionType} />}
              </TruncatedName>
            );
          }

          if (data.investmentType === 'fund') {
            return (
              <TruncatedName name={value} fullName={data.name}>
                <AssetBadge assetType={data.investment.assetType} />
              </TruncatedName>
            );
          }

          return <TruncatedName name={value} fullName={data.name} />;
        }

        default:
          return assertExhaustive(data);
      }

    case 'factor':
    case 'total':
    case 'axis':
      return <TruncatedName name={value} fullName={data.name} />;

    default:
      return assertExhaustive(data);
  }
};
